/**
 * The web side of control mode phone_handoff (ui-options/BUILD-SPEC.md, contracts/platform-api.md "Control modes"):
 * a phone sends a task to this computer: a signed-in tab picks it up and opens it, and a computer that isn't signed
 * in redeems the phone's code at /go (which also signs it in). The phone app still drives the robot.
 * Each call is made only while the mode is on; the platform answers 404 otherwise.
 */
import type { Handoff } from '@robotensor/api'
import { useQuery } from '@tanstack/react-query'
import { api } from './api.ts'

/** Polls every 2 s while the tab is visible and the mode is on (the contract's pace; no socket per account needed). */
export function usePendingHandoffs(enabled: boolean) {
  return useQuery({
    queryKey: ['handoffs', 'pending'],
    queryFn: async () => (await api.handoffs.pending()).items,
    enabled,
    refetchInterval: (q) => (q.state.error ? 30_000 : 2_000),
    refetchIntervalInBackground: false,
    retry: false,
  })
}

/** Where a handoff opens: the robot session's brief (with Start; nothing is claimed until the person presses it). */
export function handoffTarget(h: Handoff): string {
  if (h.mission_id) return `/robot/run/${encodeURIComponent(h.mission_id)}`
  if (h.task_id) return `/robot/run/${encodeURIComponent(h.task_id)}`
  return '/home'
}

/** Claims a handoff for this tab (a second open tab gets 409) and returns where to go. */
export async function acceptHandoff(id: string): Promise<string> {
  return handoffTarget(await api.handoffs.accept(id))
}

/** /go: the code the phone shows. Signs this browser in as that person and returns the task to open. */
export async function redeemDeviceCode(code: string): Promise<string> {
  return handoffTarget(await api.handoffs.redeem(normaliseCode(code)))
}

/** K7P2-QXRM, k7p2qxrm and "k7p2 qxrm" are the same code. */
export function normaliseCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '')
}
